import type { CashPlan, CashPlans } from "./cash-plan-contract.js";
import { cashPlanDates, cashPlanOccurrence, validateCashPlans } from "./cash-plan-domain.js";

export type CashPlanEditScope = "occurrence" | "future" | "all";
const values = (plan: Pick<CashPlan, "name" | "category" | "amount" | "currency" | "direction">) => ({
  name: plan.name, category: plan.category, amount: plan.amount, currency: plan.currency, direction: plan.direction,
});

/** Occurrence.date always remains the original recurrence date, including after a due-date move. */
export function editCashPlan(data: CashPlans, changed: CashPlan, scope: CashPlanEditScope = "all", occurrenceDate = changed.startDate): CashPlans {
  const current = data.plans.find(plan => plan.id === changed.id);
  if (!current) throw new Error("Düzenlenecek plan bulunamadı. Planları yeniden yükleyin.");
  if (!cashPlanDates(current, occurrenceDate).includes(occurrenceDate)) throw new Error("Düzenlenecek dönem takvimde bulunamadı.");
  let next: CashPlan;
  if (scope === "occurrence") {
    const occurrence = { ...cashPlanOccurrence(current, occurrenceDate), ...values(changed), dueDate: changed.startDate };
    next = { ...current, occurrences: [...(current.occurrences ?? []).filter(item => item.date !== occurrenceDate), occurrence] };
  } else {
    const scheduleChanged = current.startDate !== changed.startDate || current.frequency !== changed.frequency || current.endDate !== changed.endDate;
    if (scheduleChanged && (scope !== "all" || data.bindings.some(item => item.planId === current.id)
      || current.revisions?.length || current.occurrences?.length))
      throw new Error("Bu takvimin bağlı veya düzenlenmiş dönemleri var. Tek bir dönemin vadesini değiştirin; yeni tekrar düzeni için mevcut planı arşivleyip yeni plan ekleyin.");
    next = scope === "all" ? { ...current, ...values(changed), startDate: changed.startDate, endDate: changed.endDate,
      frequency: changed.frequency, revisions: current.revisions?.map(item => ({ ...item, ...values(changed) })),
      occurrences: current.occurrences?.map(item => ({ ...item, ...values(changed) })) }
      : { ...current, revisions: [...(current.revisions ?? []).filter(item => item.fromDate < occurrenceDate),
        { fromDate: occurrenceDate, ...values(changed) }],
        occurrences: current.occurrences?.map(item => item.date >= occurrenceDate ? { ...item, ...values(changed) } : item) };
  }
  const result = { ...data, plans: data.plans.map(plan => plan.id === current.id ? next : plan) };
  validateCashPlans(result, undefined, data);
  return result;
}

export function cancelCashPlan(data: CashPlans, planId: string, fromDate: string, onlyOccurrence = false): CashPlans {
  const current = data.plans.find(plan => plan.id === planId);
  if (!current) throw new Error("Plan bulunamadı.");
  if (onlyOccurrence && !cashPlanDates(current, fromDate).includes(fromDate)) throw new Error("İptal edilecek dönem bulunamadı.");
  const next = onlyOccurrence ? { ...current, occurrences: [...(current.occurrences ?? []).filter(item => item.date !== fromDate),
    { ...cashPlanOccurrence(current, fromDate), cancelled: true }] }
    : { ...current, archivedFrom: fromDate };
  return { ...data, plans: data.plans.map(plan => plan.id === planId ? next : plan) };
}

export function restoreCashPlan(data: CashPlans, planId: string, date?: string): CashPlans {
  return { ...data, plans: data.plans.map(plan => {
    if (plan.id !== planId) return plan;
    if (date) return { ...plan, occurrences: plan.occurrences?.map(item => item.date === date ? { ...item, cancelled: false } : item) };
    const restored = { ...plan, active: true };
    delete restored.archivedFrom;
    return restored;
  }) };
}
